/**
 * The table's own column switch.
 *
 * Modelled on `FilterMenu` rather than sharing it: that one is a many-of-N
 * FILTER, whose trigger counts how much the list below is narrowed. This one
 * changes nothing about which companies are listed, so counting ticks would say
 * the wrong thing - it says how many columns are on, and only shouts when the
 * layout is no longer the standard one.
 *
 * The menu stays open while columns are ticked: picking a table layout is
 * several decisions, and one column at a time with a re-open between each is
 * exactly the annoyance a picker is meant to remove.
 */
import { Check, Columns3, RotateCcw } from "lucide-react";
import { cn } from "@wa/ui";
import type { useT } from "../../../i18n/I18nProvider";
import { Pop } from "../../shell/Pop";
import { AR_COLUMNS } from "./columns";

export function ColumnPicker({
  visible,
  onToggle,
  onReset,
  changed,
  t,
}: {
  visible: Set<string>;
  onToggle: (key: string) => void;
  onReset: () => void;
  changed: boolean;
  t: ReturnType<typeof useT>;
}) {
  return (
    <Pop
      trigger={(open) => (
        <button
          type="button"
          className={cn(
            "crm-fld crm-fld-sel w-auto min-w-0 gap-2.5 px-4 text-[12.75px]",
            (open || changed) && "is-on",
          )}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <Columns3 className="size-4" />
          <span>{t("ariregister.columns.label")}</span>
          <span className="crm-fld-n">{visible.size}</span>
        </button>
      )}
    >
      {AR_COLUMNS.map((col) => {
        const on = visible.has(col.key);
        return (
          <button
            key={col.key}
            type="button"
            role="menuitemcheckbox"
            aria-checked={on}
            disabled={col.locked}
            onClick={() => onToggle(col.key)}
            className={cn("crm-mi", on && "is-on", col.locked && "cursor-default opacity-60")}
            // The name column is ticked and cannot be unticked - said out loud,
            // because a checkbox that ignores a click looks broken otherwise.
            title={col.locked ? t("ariregister.columns.locked") : undefined}
          >
            <span className="crm-trunc flex-1">{t(`ariregister.col.${col.label}`)}</span>
            {/* Kept in the layout when off, or every row shifts sideways as the
                tick appears and disappears under the pointer. */}
            <Check className={cn("size-4", !on && "opacity-0")} />
          </button>
        );
      })}

      {changed ? (
        <>
          <div className="crm-menu-sep" />
          <button type="button" className="crm-mi" onClick={onReset}>
            <RotateCcw className="size-4" />
            {t("ariregister.columns.reset")}
          </button>
        </>
      ) : null}
    </Pop>
  );
}
